import React, {Fragment, useEffect, useMemo, useState} from 'react';
import {Flex, Text} from '@chakra-ui/react';
import {useDebounce} from 'usehooks-ts';
import {useAccountTypeahead} from '../hooks/api/accountTypeahead';
import {CompanyLogo} from './Icons/CompanyLogo';
import {ObjectId} from 'bson';
import CreatableSelect from 'react-select/creatable';
import {isFQDN} from 'validator';
import {
  getTypeaheadStyles,
  TypeaheadDropdownIndicator,
} from './utils/typeahead';

type OptionType = {value: DomainSelection; label: string};

const LoadingIndicator = () => <></>;

const formatOptionLabel = (data: {value: DomainSelection; label: string}) => {
  const {domain, name} = data.value;
  return (
    <Flex alignItems="center" gap={2} ml={1}>
      <CompanyLogo
        domain={domain}
        name={name ?? domain}
        boxSize="40px"
        mr="2"
      />
      <Text fontWeight="thin" fontSize="14px">
        {name ?? domain}
      </Text>
      {name && (
        <Text fontWeight="thin" fontSize="14px" color="gray.500">
          {domain}
        </Text>
      )}
    </Flex>
  );
};

export type DomainSelection = {
  domain: string;
  accountId?: ObjectId;
  name?: string;
};

export const CreatableDomainTypeahead = ({
  selectedDomain,
  onDomainSelect,
  placeholder = 'Start typing to search for a website',
  isDisabled,
}: {
  selectedDomain: DomainSelection | undefined;
  onDomainSelect: (domain: DomainSelection | undefined) => void;
  placeholder?: string;
  isDisabled?: boolean;
}) => {
  const [search, setSearch] = useState('');
  const [displayedDomain, setDisplayedDomain] = useState(
    selectedDomain ?? undefined
  );

  useEffect(() => {
    setDisplayedDomain(selectedDomain);
  }, [selectedDomain, setDisplayedDomain]);

  const debouncedSearch = useDebounce<string>(search, 200);
  const accountTypeaheadQuery = useAccountTypeahead(debouncedSearch);
  const typeaheadAccounts = accountTypeaheadQuery.data;

  const typeaheadOptions = useMemo(
    () =>
      typeaheadAccounts?.map((account) => ({
        value: {
          domain: account.domain,
          accountId: account._id,
          name: account.name,
        },
        label: account.domain,
      })) ?? [],
    [typeaheadAccounts]
  );

  return (
    <CreatableSelect<OptionType, false>
      components={{
        DropdownIndicator: TypeaheadDropdownIndicator,
        IndicatorSeparator: null,
        LoadingIndicator,
      }}
      controlShouldRenderValue={true}
      formatCreateLabel={(input) => input}
      formatOptionLabel={formatOptionLabel}
      getNewOptionData={(input) => {
        return {value: {domain: input}, label: input};
      }}
      hideSelectedOptions={true}
      inputValue={search}
      isClearable={true}
      isDisabled={isDisabled}
      isLoading={accountTypeaheadQuery.isFetching}
      isMulti={false}
      isValidNewOption={(input) => {
        return (
          // Wait for the debounced value to match the input
          search === debouncedSearch &&
          !typeaheadOptions.some(({value}) => value.domain === input) &&
          isFQDN(input)
        );
      }}
      loadingMessage={() => null}
      menuPortalTarget={document.body}
      noOptionsMessage={() => null}
      onChange={(selected) => {
        setDisplayedDomain(selected?.value);
        onDomainSelect(selected?.value);
      }}
      onCreateOption={(input) => {
        setDisplayedDomain({domain: input});
        onDomainSelect({domain: input});
      }}
      onInputChange={(val: string) => setSearch(val)}
      options={typeaheadOptions}
      placeholder={placeholder}
      styles={getTypeaheadStyles<OptionType, false>()}
      value={
        displayedDomain
          ? {
              value: displayedDomain,
              label: displayedDomain.name ?? displayedDomain.domain,
            }
          : undefined
      }
    />
  );
};
